import axios from 'axios';
import { Dispatch, FC, SetStateAction } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import { EmojiPlugin } from '@draft-js-plugins/emoji';
import { Grid, Typography } from '@mui/material';
import DraftJsInput, { IDraftJsInputProps } from 'components/inputs/draft-js-input';
import useToast from 'hooks/useToast';
import { ErrorCode } from 'react-dropzone';
import { MAX_FILE_COUNT, MAX_FILE_SIZE } from 'utils/constants';
import { IUploadingFiles } from 'utils/types';
import { getAccessToken, isLoginAsSession } from 'utils/authData';
import {
  assertAuthStateMatchesRefreshedAccessToken,
  clearAuthAndRedirectAfterRefreshFailure,
  clearAuthAndRedirectToSignIn,
  getAuthSessionIdentity,
  getLatestAccessTokenAfterAuthChange,
  hasAuthSessionIdentityChanged,
  refreshAccessToken,
  resolveLoginAsRequestAuthFailure,
} from 'utils/authRefresh';
import { textEditorStyles } from './styles/styles';

interface ITextEditorProps extends IDraftJsInputProps {
  setUploadedFiles: Dispatch<SetStateAction<IUploadingFiles[]>>;
  draggableAreaHeight?: number;
  saveHeight?: boolean;
  emojiPlugin: EmojiPlugin;
  projectId: number;
}

const getUploadResponseData = (data: string, tempId: string) => {
  if (!data) {
    return { tempId };
  }

  try {
    return { ...JSON.parse(data), tempId };
  } catch {
    return { tempId };
  }
};

const TextEditor: FC<ITextEditorProps> = ({
  editorState,
  onChange,
  onBlur,
  onSubmit,
  autofocus,
  placeholder,
  isError,
  inputStyles,
  draggableAreaHeight,
  setUploadedFiles,
  mentionsList,
  saveHeight,
  emojiPlugin,
  projectId,
}) => {
  const showToast = useToast();

  // Create proper Accept object from constants
  const acceptConfig = {
    'image/jpg': ['.jpg'],
    'image/jpeg': ['.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/tiff': ['.tiff'],
    'image/avif': ['.avif'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'application/pdf': ['.pdf'],
    'text/csv': ['.csv'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.apple.keynote': ['.key'],
  };

  const { getRootProps, isDragActive } = useDropzone({
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    accept: acceptConfig as any,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length) {
        const isInvalidFile = rejectedFiles.find(({ errors }) =>
          errors.find(({ code }) => code === ErrorCode.FileInvalidType)
        );
        if (isInvalidFile) {
          showToast({ type: 'error', message: 'This file type is not supported.' });
        }
        const filteredRejectedFiles = rejectedFiles.filter(({ errors }) =>
          errors.find(({ code }) => code === ErrorCode.FileTooLarge)
        );
        const rejectedUploads: IUploadingFiles[] = filteredRejectedFiles.map(({ file }) => ({
          size: file.size,
          name: file.name,
          status: 'error_file_size',
          percent: 0,
          id: '',
          url: '',
          tempId: uuidv4(),
        }));
        setUploadedFiles((prev) => prev.concat(rejectedUploads));
      }

      if (!acceptedFiles?.length) {
        return;
      }

      const { convertHeicFiles } = await import('utils/convertHeicToJpeg');
      const convertedFiles = await convertHeicFiles(acceptedFiles);

      if (convertedFiles.length > MAX_FILE_COUNT) {
        showToast({ type: 'error', message: 'Too many files.' });
      }

      const files = convertedFiles.slice(0, MAX_FILE_COUNT).map((file) => ({
        tempId: uuidv4(),
        file,
      }));

      setUploadedFiles((prev) =>
        prev.concat(
          files.map(({ file: { name, size }, tempId }) => ({
            id: '',
            url: '',
            status: 'preparing',
            size,
            name,
            tempId,
            percent: 0,
          }))
        )
      );

      try {
        const promises = files.map(async ({ file, tempId }) => {
          const formData = new FormData();
          formData.append('file', file);
          const wasLoginAsRequest = isLoginAsSession();
          const requestAccessToken = getAccessToken();
          const authSessionIdentity = getAuthSessionIdentity(requestAccessToken);

          let response = await axios({
            method: 'POST',
            baseURL: `${process.env.REACT_APP_ENV_API_URL}`,
            url: '/uploads',
            headers: {
              Authorization: requestAccessToken,
              'Content-Type': 'multipart/form-data',
            },
            data: formData,
            onUploadProgress: ({ loaded, total }) => {
              const percent =
                typeof loaded === 'number' && typeof total === 'number'
                  ? Math.round((loaded / total) * 100)
                  : 0;
              setUploadedFiles((prev) =>
                prev.map((item) => {
                  if (item.tempId === tempId) {
                    return { ...item, percent: percent, status: 'uploading' };
                  }
                  return item;
                })
              );
            },
            transformResponse: (data) => {
              return getUploadResponseData(data, tempId);
            },
            validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
          });

          if (response.status === 401) {
            if (wasLoginAsRequest) {
              resolveLoginAsRequestAuthFailure();
              throw new Error('Unauthorized');
            }

            if (hasAuthSessionIdentityChanged(authSessionIdentity)) {
              throw new Error('Unauthorized');
            }

            let refreshedToken: string;
            const latestAccessToken = getLatestAccessTokenAfterAuthChange(requestAccessToken);
            if (latestAccessToken) {
              refreshedToken = latestAccessToken;
            } else {
              try {
                refreshedToken = await refreshAccessToken();
                assertAuthStateMatchesRefreshedAccessToken(refreshedToken);
              } catch (error) {
                clearAuthAndRedirectAfterRefreshFailure(error);
                throw new Error('Unauthorized');
              }
            }

            response = await axios({
              method: 'POST',
              baseURL: `${process.env.REACT_APP_ENV_API_URL}`,
              url: '/uploads',
              headers: {
                Authorization: refreshedToken,
                'Content-Type': 'multipart/form-data',
              },
              data: formData,
              onUploadProgress: ({ loaded, total }) => {
                const percent =
                  typeof loaded === 'number' && typeof total === 'number'
                    ? Math.round((loaded / total) * 100)
                    : 0;
                setUploadedFiles((prev) =>
                  prev.map((item) => {
                    if (item.tempId === tempId) {
                      return { ...item, percent: percent, status: 'uploading' };
                    }
                    return item;
                  })
                );
              },
              transformResponse: (data) => {
                return getUploadResponseData(data, tempId);
              },
              validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
            });

            if (response.status === 401) {
              if (hasAuthSessionIdentityChanged(authSessionIdentity)) {
                throw new Error('Unauthorized');
              }

              clearAuthAndRedirectToSignIn();
              throw new Error('Unauthorized');
            }
          }

          return response;
        });

        const response: any[] = await Promise.all(promises);

        response.forEach(({ data }) => {
          setUploadedFiles((prev) =>
            prev.map((item) => {
              if (item.tempId === data.tempId) {
                return {
                  ...item,
                  percent: 100,
                  status: 'done',
                  id: data.id,
                  url: data.url,
                };
              }
              return item;
            })
          );
        });
      } catch (err) {
        files.forEach(({ tempId }) => {
          setUploadedFiles((prev) =>
            prev.map((item) => {
              if (tempId === item.tempId) {
                return {
                  ...item,
                  status: 'error_upload',
                };
              }
              return item;
            })
          );
        });
        showToast({
          message:
            typeof err?.data?.message === 'string'
              ? err.data.message
              : 'Something went wrong.  Please try again or contact support for help.',
          type: 'error',
        });
      }
    },
  });

  return (
    <Grid {...getRootProps()} sx={{ display: 'flex', width: '100%' }}>
      {isDragActive ? (
        <Grid
          sx={{
            ...textEditorStyles.container,
            height: draggableAreaHeight ? `${draggableAreaHeight}px` : '195px',
          }}
        >
          <Typography sx={textEditorStyles.title}>Drag & drop your file here</Typography>
          <Typography sx={textEditorStyles.subtitle}>Maximum file size 50 MB</Typography>
        </Grid>
      ) : (
        <DraftJsInput
          projectId={projectId}
          autofocus={autofocus}
          isError={isError}
          inputStyles={inputStyles}
          placeholder={placeholder}
          editorState={editorState}
          onChange={onChange}
          onBlur={onBlur}
          onSubmit={onSubmit}
          mentionsList={mentionsList}
          saveHeight={saveHeight}
          emojiPlugin={emojiPlugin}
        />
      )}
    </Grid>
  );
};

export default TextEditor;
